/* The Smooth Geezer — shared behaviours for the sg-* sections.
   Carousel (scroll-snap), accordion, marquee, load-more, inline video, sticky ATC.
   Idempotent: every init is guarded so shopify:section:load can re-run it. */
(function () {
  'use strict';
  var SG = window.SG = window.SG || {};

  /* ---------- carousel: Swiper (loaded in theme.liquid) with a scroll-snap fallback ---------- */
  function initCarousel(root) {
    if (root.__sgCarousel) return;
    var track = root.querySelector('[data-sg-track]');
    if (!track) return;
    root.__sgCarousel = true;
    var own = function (sel) { return [].slice.call(root.querySelectorAll(sel)).filter(function (el) { return el.closest('[data-sg-carousel]') === root; }); };
    var prevs = own('[data-sg-prev]'), nexts = own('[data-sg-next]');
    var loop = root.hasAttribute('data-sg-loop');
    if (window.Swiper && !root.hasAttribute('data-sg-native')) return initSwiper(root, track, prevs, nexts, loop);
    return initNative(root, track, prevs, nexts, loop);
  }

  function initSwiper(root, track, prevs, nexts, loop) {
    var cs = getComputedStyle(track);
    var gap = parseFloat(cs.columnGap || cs.gap) || 0;
    var bleed = parseFloat(cs.marginLeft) < 0;
    var pad = bleed ? (parseFloat(cs.paddingLeft) || 0) : 0;
    var centeredAttr = root.getAttribute('data-sg-centered');
    var centered = centeredAttr !== null && (centeredAttr !== 'mobile' || window.matchMedia('(max-width: 989px)').matches);
    var container = document.createElement('div');
    container.className = 'swiper sg-swiper' + (bleed ? ' sg-swiper--bleed' : '');
    track.parentNode.insertBefore(container, track);
    container.appendChild(track);
    track.classList.add('swiper-wrapper');
    [].forEach.call(track.children, function (li) { li.classList.add('swiper-slide'); });
    var sw = new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: gap,
      speed: 650,
      grabCursor: true,
      rewind: loop,
      centeredSlides: centered,
      slidesOffsetBefore: centered ? 0 : pad,
      slidesOffsetAfter: centered ? 0 : pad,
      watchOverflow: true,
      resistanceRatio: 0.65,
      threshold: 5,
      longSwipesRatio: 0.2,
      navigation: { prevEl: prevs, nextEl: nexts, disabledClass: 'is-disabled' },
      mousewheel: { forceToAxis: true, sensitivity: 1, releaseOnEdges: true },
      a11y: { enabled: true }
    });
    root.__swiper = sw;
    root.classList.add('sg-has-swiper');
    /* slide widths come from CSS; re-measure after fonts/images settle */
    setTimeout(function () { sw.update(); }, 300);
    window.addEventListener('load', function () { sw.update(); });
  }

  function initNative(root, track, prevs, nexts, loop) {
    function step() {
      var first = track.children[0];
      if (!first) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }
    function atStart() { return track.scrollLeft <= 2; }
    function atEnd() { return track.scrollLeft + track.clientWidth >= track.scrollWidth - 2; }
    function update() {
      if (loop) return;
      prevs.forEach(function (b) { b.disabled = atStart(); });
      nexts.forEach(function (b) { b.disabled = atEnd(); });
    }
    function go(dir) {
      if (dir > 0 && atEnd() && loop) { track.scrollTo({ left: 0, behavior: 'smooth' }); return; }
      if (dir < 0 && atStart() && loop) { track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' }); return; }
      track.scrollBy({ left: dir * step(), behavior: 'smooth' });
    }
    prevs.forEach(function (b) { b.addEventListener('click', function () { go(-1); }); });
    nexts.forEach(function (b) { b.addEventListener('click', function () { go(1); }); });
    track.addEventListener('scroll', throttle(update, 80), { passive: true });
    window.addEventListener('resize', throttle(update, 150));
    update();

    /* progress bar (comparison table on mobile) */
    var bar = root.querySelector('[data-sg-progress]');
    if (bar) {
      var paint = function () {
        var max = track.scrollWidth - track.clientWidth;
        var pct = max <= 0 ? 1 : track.clientWidth / track.scrollWidth;
        var pos = max <= 0 ? 0 : track.scrollLeft / max;
        bar.style.setProperty('--sg-thumb-w', (pct * 100).toFixed(2) + '%');
        bar.style.setProperty('--sg-thumb-x', (pos * (1 - pct) * 100).toFixed(2) + '%');
        root.classList.toggle('sg-no-scroll', max <= 0);
      };
      track.addEventListener('scroll', throttle(paint, 40), { passive: true });
      window.addEventListener('resize', throttle(paint, 150));
      paint();
    }
    /* drag to scroll on desktop (no smooth behaviour while dragging) */
    var isDown = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('mousedown', function (e) { isDown = true; moved = false; startX = e.pageX; startLeft = track.scrollLeft; track.classList.add('is-dragging'); });
    window.addEventListener('mouseup', function () { if (!isDown) return; isDown = false; track.classList.remove('is-dragging'); });
    track.addEventListener('mousemove', function (e) { if (!isDown) return; var dx = e.pageX - startX; if (Math.abs(dx) > 4) moved = true; track.scrollLeft = startLeft - dx; });
    track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
  }

  /* ---------- accordion (details-based; measured height animation; keeps the clicked row in place) ---------- */
  var EASE = 'cubic-bezier(.22,.61,.36,1)';
  function panelOf(d) { return d.querySelector('.sg-acc__panel'); }
  function openItem(d) {
    var panel = panelOf(d);
    d.open = true;
    d.classList.add('is-open');
    var btn = d.querySelector('summary'); if (btn) btn.setAttribute('aria-expanded', 'true');
    if (!panel || !panel.animate) return;
    if (panel.__anim) panel.__anim.cancel();
    var h = panel.scrollHeight;
    panel.style.overflow = 'hidden';
    panel.__anim = panel.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 420, easing: EASE });
    panel.__anim.onfinish = function () { panel.style.overflow = ''; panel.__anim = null; };
    d.dispatchEvent(new CustomEvent('sg:open', { bubbles: true }));
  }
  function closeItem(d) {
    var panel = panelOf(d);
    d.classList.remove('is-open');
    var btn = d.querySelector('summary'); if (btn) btn.setAttribute('aria-expanded', 'false');
    d.dispatchEvent(new CustomEvent('sg:close', { bubbles: true }));
    if (!panel || !panel.animate) { d.open = false; return; }
    if (panel.__anim) panel.__anim.cancel();
    var h = panel.getBoundingClientRect().height;
    panel.style.overflow = 'hidden';
    var done = false;
    var finish = function () { if (done) return; done = true; panel.style.overflow = ''; panel.__anim = null; d.open = false; };
    panel.__anim = panel.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 360, easing: EASE });
    panel.__anim.onfinish = finish;
    setTimeout(finish, 420);                 /* source of truth — onfinish is only an early exit */
  }
  function initAccordionGroup(group) {
    if (group.__sgAcc) return;
    group.__sgAcc = true;
    group.style.overflowAnchor = 'none';
    var single = group.hasAttribute('data-sg-single');
    var items = [].slice.call(group.querySelectorAll('details.sg-acc'));
    items.forEach(function (d) {
      var summary = d.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        var anchorTop = summary.getBoundingClientRect().top;
        if (d.open) { closeItem(d); }
        else {
          if (single) items.forEach(function (o) { if (o !== d && o.open) closeItem(o); });
          openItem(d);
        }
        /* keep the clicked row where the finger/cursor is while rows above it collapse */
        var t0 = performance.now();
        var keep = function () {
          var delta = summary.getBoundingClientRect().top - anchorTop;
          if (Math.abs(delta) > 0.5) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
          if (performance.now() - t0 < 560) requestAnimationFrame(keep);
        };
        requestAnimationFrame(keep);
      });
      if (d.open) d.classList.add('is-open');
    });
  }

  /* ---------- marquee ---------- */
  // The keyframe slides the track by -50%, so the track must hold an EVEN number of identical copies: n copies per half,
  // n = enough to cover the container. Re-measured on resize (and when the element goes display:none → block across the
  // desktop/mobile breakpoint — at init on desktop the mobile track is hidden and measures 0 wide).
  function initMarquee(el) {
    if (el.__sgMarquee) return;
    el.__sgMarquee = true;
    var track = el.querySelector('.sg-marquee__track');
    if (!track) return;
    var html = track.innerHTML, copies = 0, lastWidth = -1;
    function measure() {
      var w = el.clientWidth;
      if (!w) return;                           // hidden at this breakpoint — try again when it shows
      if (w === lastWidth) return;
      lastWidth = w;
      track.innerHTML = html;                   // one copy → measure it
      var one = track.scrollWidth || 1;
      var n = Math.max(1, Math.ceil(w / one));
      if (n * 2 !== copies) {
        var out = '';
        for (var i = 0; i < n * 2; i++) out += html;
        track.innerHTML = out;
        copies = n * 2;
      } else {
        track.innerHTML = new Array(copies + 1).join(html);
      }
      var speed = parseFloat(el.getAttribute('data-sg-speed')) || 60; // px per second
      el.style.setProperty('--sg-marquee-duration', ((track.scrollWidth / 2) / speed).toFixed(2) + 's');
    }
    measure();
    window.addEventListener('resize', throttle(measure, 150));
    if ('ResizeObserver' in window) new ResizeObserver(function () { measure(); }).observe(el);
  }

  /* ---------- load more ---------- */
  function initLoadMore(root) {
    if (root.__sgMore) return;
    root.__sgMore = true;
    var btn = root.querySelector('[data-sg-more]');
    if (!btn) return;
    var per = parseInt(btn.getAttribute('data-sg-more'), 10) || 3;
    btn.addEventListener('click', function () {
      var hidden = [].slice.call(root.querySelectorAll('[data-sg-hidden]'));
      hidden.slice(0, per).forEach(function (el) { el.removeAttribute('data-sg-hidden'); });
      if (root.querySelectorAll('[data-sg-hidden]').length === 0) { btn.hidden = true; root.classList.add('is-complete'); }
    });
  }

  /* ---------- video card: Shopify <video> plays in place; else poster -> iframe/video from a URL ---------- */
  function stopOthers(except) {
    document.querySelectorAll('[data-sg-video].is-playing').forEach(function (other) {
      if (other === except) return;
      var v = other.querySelector('video'); if (v) { v.pause(); }
      var p = other.querySelector('.sg-video__player'); if (p) p.remove();
      other.classList.remove('is-playing');
    });
  }
  function initVideo(el) {
    if (el.__sgVideo) return;
    el.__sgVideo = true;
    var btn = el.querySelector('[data-sg-play]') || el;
    var native = el.querySelector('video');
    if (native) {
      native.addEventListener('play', function () { stopOthers(el); el.classList.add('is-playing'); });
      native.addEventListener('pause', function () { if (native.ended || native.currentTime === 0) el.classList.remove('is-playing'); });
      native.addEventListener('ended', function () { el.classList.remove('is-playing'); });
    }
    btn.addEventListener('click', function (e) {
      if (e.target.closest('video')) return;          /* native controls */
      e.preventDefault();
      if (native) { stopOthers(el); el.classList.add('is-playing'); native.muted = false; var p = native.play(); if (p && p.catch) p.catch(function () { native.muted = true; native.play(); }); return; }
      var src = el.getAttribute('data-sg-video');
      if (!src) return;
      var host = el.querySelector('[data-sg-video-host]') || el;
      var yt = src.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
      var vm = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      var node;
      if (yt) { node = document.createElement('iframe'); node.src = 'https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&playsinline=1&rel=0'; node.allow = 'autoplay; encrypted-media; picture-in-picture'; node.setAttribute('allowfullscreen', ''); }
      else if (vm) { node = document.createElement('iframe'); node.src = 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=1&playsinline=1'; node.allow = 'autoplay; fullscreen; picture-in-picture'; node.setAttribute('allowfullscreen', ''); }
      else { node = document.createElement('video'); node.src = src; node.controls = true; node.autoplay = true; node.playsInline = true; }
      node.className = 'sg-video__player';
      host.appendChild(node);
      stopOthers(el);
      el.classList.add('is-playing');
    });
  }

  /* ---------- sticky bar (shows once a sentinel has scrolled above the viewport) ---------- */
  function initSticky(bar) {
    if (bar.__sgSticky) return;
    bar.__sgSticky = true;
    var sel = bar.getAttribute('data-sg-sticky');
    var sentinel = sel ? document.querySelector(sel) : null;
    if (!sentinel) { bar.classList.add('is-visible'); return; }
    var footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
    var check = function () {
      var past = sentinel.getBoundingClientRect().bottom < 0;
      var atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight - 20 : false;
      bar.classList.toggle('is-visible', past && !atFooter);
    };
    window.addEventListener('scroll', throttle(check, 100), { passive: true });
    window.addEventListener('resize', throttle(check, 200));
    if ('IntersectionObserver' in window) new IntersectionObserver(check, { threshold: 0 }).observe(sentinel);
    check();
  }

  /* ---------- quantity ---------- */
  function initQty(q) {
    if (q.__sgQty) return; q.__sgQty = true;
    var input = q.querySelector('input');
    q.querySelector('[data-sg-minus]').addEventListener('click', function () { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); input.dispatchEvent(new Event('change', { bubbles: true })); });
    q.querySelector('[data-sg-plus]').addEventListener('click', function () { input.value = (parseInt(input.value, 10) || 1) + 1; input.dispatchEvent(new Event('change', { bubbles: true })); });
    input.addEventListener('change', function () { if (!(parseInt(input.value, 10) > 0)) input.value = 1; });
  }

  /* ---------- reveal: the theme's scroll animation uses a 40% visibility threshold, which tall sections never reach
     (they'd stay invisible). Reveal our sections as soon as any part enters the viewport. ---------- */
  function initReveal() {
    if (document.__sgReveal) return; document.__sgReveal = true;
    var secs = [].slice.call(document.querySelectorAll('.shopify-section')).filter(function (s) { return s.querySelector(':scope > .sg'); });
    if (!secs.length) return;
    var show = function (s) { s.classList.add('section-animated', 'sg-revealed'); };
    if (!('IntersectionObserver' in window)) { secs.forEach(show); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { show(en.target); io.unobserve(en.target); } });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    secs.forEach(function (s) {
      var r = s.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) show(s); else io.observe(s);
    });
    /* belt and braces for very fast flings: sweep on scroll too */
    var sweep = throttle(function () {
      secs = secs.filter(function (s) {
        if (s.classList.contains('sg-revealed')) return false;
        var r = s.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.92 && r.bottom > 0) { show(s); io.unobserve(s); return false; }
        return true;
      });
    }, 150);
    window.addEventListener('scroll', sweep, { passive: true });
  }

  /* ---------- free-gift quantity sync: gift lines (_free_gift) always mirror the quantity of the product they came with ---------- */
  var giftSyncBusy = false, giftSyncTimer = null;
  function syncFreeGifts() {
    if (giftSyncBusy) return;
    clearTimeout(giftSyncTimer);
    giftSyncTimer = setTimeout(function () {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }).then(function (cart) {
        var items = cart.items || [];
        var gifts = items.filter(function (i) { return i.properties && i.properties._free_gift; });
        if (!gifts.length) return;
        var change = null;
        gifts.some(function (g, gi) {
          var forId = g.properties._free_gift_for ? parseInt(g.properties._free_gift_for, 10) : null;
          var per = parseInt(g.properties._free_gift_per || '1', 10) || 1;
          var mains = items.filter(function (i) { return !(i.properties && i.properties._free_gift) && (forId ? i.variant_id === forId : true); });
          var mainQty = forId ? mains.reduce(function (n, i) { return n + i.quantity; }, 0) : (mains[0] ? mains[0].quantity : 0);
          var target = mainQty * per;
          if (target !== g.quantity) { change = { line: items.indexOf(g) + 1, key: g.key, quantity: target }; return true; }
          return false;
        });
        if (!change) return;
        giftSyncBusy = true;
        var el = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
        var done = function () { setTimeout(function () { giftSyncBusy = false; syncFreeGifts(); }, 700); };
        if (el && typeof el.updateQuantity === 'function') {
          try { el.updateQuantity(change.line, change.quantity); done(); return; } catch (e) {}
        }
        /* SG cart drawer (Surge copy): change through it so it re-renders and broadcasts cart:updated */
        if (window.SGCart && typeof window.SGCart.changeLine === 'function' && !document.querySelector('.main-cart[data-section-id]')) {
          var pr0 = window.SGCart.changeLine(change.line, change.quantity); if (pr0 && pr0.then) pr0.then(done, done); else done(); return;
        }
        /* Surge cart page: reuse its own line updater so the table re-renders through the section API */
        var surgeCart = document.querySelector('.main-cart[data-section-id]');
        if (surgeCart && typeof lineItemUpdate === 'function') {
          try { lineItemUpdate(change.line, change.quantity, JSON.stringify([{ id: surgeCart.dataset.sectionId, selectors: ['[data-line-items-container]'] }]), '[data-line-items-container]'); done(); return; } catch (e) {}
        }
        /* Surge theme: its cart state re-fetches the cart and re-renders the drawer + bubble on cart:updated */
        if (window.VanillaCartState && typeof window.VanillaCartState.updateItemByLine === 'function' && document.querySelector('cart-slide')) {
          var pr = window.VanillaCartState.updateItemByLine(change.line, change.quantity);
          if (pr && pr.then) pr.then(done, done); else done();
          return;
        }
        var cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        var sections = (cartEl && typeof cartEl.getSectionsToRender === 'function') ? cartEl.getSectionsToRender().map(function (s) { return s.id; }).join(',') : '';
        fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: change.key, quantity: change.quantity, sections: sections, sections_url: window.location.pathname }) })
          .then(function (r) { return r.json(); })
          .then(function (res) { if (cartEl && typeof cartEl.renderContents === 'function' && res.sections) cartEl.renderContents(res); else if (document.body.classList.contains('template-cart') || /\/cart\b/.test(location.pathname)) location.reload(); })
          .catch(function () {}).then(done);
      }).catch(function () { giftSyncBusy = false; });
    }, 350);
  }
  function initGiftSync() {
    if (document.__sgGiftSync) return; document.__sgGiftSync = true;
    var watch = function (el) { if (!el || el.__sgGiftObs) return; el.__sgGiftObs = true; new MutationObserver(function () { syncFreeGifts(); }).observe(el, { childList: true, subtree: true }); };
    watch(document.querySelector('cart-drawer'));
    watch(document.querySelector('cart-items'));
    watch(document.querySelector('cart-slide'));
    document.addEventListener('cart:updated', syncFreeGifts);
    document.addEventListener('sg:added', syncFreeGifts);
    syncFreeGifts();
  }

  function throttle(fn, ms) { var t = 0, pending; return function () { var now = Date.now(); clearTimeout(pending); if (now - t > ms) { t = now; fn(); } else { pending = setTimeout(function () { t = Date.now(); fn(); }, ms); } }; }

  /* ---------- smooth in-page scroll for [data-sg-scroll] anchors (offset for the sticky header) ---------- */
  function initScrollLinks() {
    if (document.__sgScrollLinks) return; document.__sgScrollLinks = true;
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[data-sg-scroll], .sg a[href^="#"]');
      if (!a) return;
      var id = (a.getAttribute('href') || '').replace(/^#/, ''); if (!id) return;
      var target = document.getElementById(id); if (!target) return;
      e.preventDefault();
      var hdr = document.querySelector('.sg-section-header');
      var offset = hdr ? hdr.getBoundingClientRect().height + 10 : 90;
      var rect = target.getBoundingClientRect();
      var top = rect.top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      if (history.replaceState) history.replaceState(null, '', '#' + id);
    });
  }

  function initAll(scope) {
    scope = scope || document;
    initScrollLinks();
    scope.querySelectorAll('[data-sg-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-sg-accordion]').forEach(initAccordionGroup);
    scope.querySelectorAll('.sg-marquee').forEach(initMarquee);
    scope.querySelectorAll('[data-sg-load-more]').forEach(initLoadMore);
    scope.querySelectorAll('[data-sg-video]').forEach(initVideo);
    scope.querySelectorAll('[data-sg-sticky]').forEach(initSticky);
    scope.querySelectorAll('[data-sg-qty]').forEach(initQty);
    initReveal();
    initGiftSync();
  }
  SG.init = initAll;
  SG.throttle = throttle;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();

/* SG header cart badge: the theme's own cart JS (Elevate cart-drawer / Surge) rewrites the badge text after an add-to-cart; reveal/hide it to match the number (it renders hidden when the cart is empty). */
(function () {
  var b = document.querySelector('#cart-icon-bubble .cart-count-bubble'); if (!b) return;
  var sync = function () { var n = parseInt((b.querySelector('span') || b).textContent, 10) || 0; b.style.setProperty('display', n > 0 ? 'flex' : 'none', 'important'); };
  new MutationObserver(sync).observe(b, { childList: true, subtree: true, characterData: true });
  document.addEventListener('cart:updated', sync);
})();
